import { Component, DestroyRef, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import {
  collectSeoFromRouteSnapshot,
  findResolvedBlogPost,
} from '@core/seo/collect-seo-from-route.util';
import { SeoService } from '@core/seo/seo.service';
import { buildBlogPostArticleJsonLd } from '@app/features/blog/utils/blog-post-seo.util';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly seo = inject(SeoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private hasAttemptedChunkRecovery = false;

  ngOnInit(): void {
    const applySeo = (): void => {
      const data = collectSeoFromRouteSnapshot(this.router.routerState.snapshot.root);
      this.seo.applyFromRouteData(data, this.router.url);

      const resolvedPost = findResolvedBlogPost(this.router.routerState.snapshot.root);
      if (resolvedPost) {
        this.seo.applyArticleJsonLd(buildBlogPostArticleJsonLd(resolvedPost.post));
      } else {
        this.seo.clearArticleJsonLd();
      }
    };
    applySeo();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(applySeo);

    this.router.events
      .pipe(
        filter((e): e is NavigationError => e instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.recoverFromStaleChunk(event.error));
  }

  /**
   * Post-deploy cache mismatch safety net:
   * a stale HTML shell can reference chunk files that no longer exist.
   * Force one hard reload to fetch the latest asset graph.
   */
  private recoverFromStaleChunk(err: unknown): void {
    if (!isPlatformBrowser(this.platformId) || this.hasAttemptedChunkRecovery) return;
    const msg = String(
      err && typeof err === 'object' && 'message' in err
        ? (err as { message?: unknown }).message ?? ''
        : err ?? '',
    ).toLowerCase();

    const isChunkOrModuleLoadFailure =
      msg.includes('chunkloaderror') ||
      msg.includes('loading chunk') ||
      msg.includes('failed to fetch dynamically imported module') ||
      msg.includes('importing a module script failed');

    if (!isChunkOrModuleLoadFailure) return;
    this.hasAttemptedChunkRecovery = true;
    window.location.reload();
  }
}
